import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTenant } from "@/hooks/useTenant";
import { cn } from "@/lib/utils";
import { COUNTRY_DIAL_CODES, PREFERRED_ISOS, findCountryByIso, type CountryDialCode } from "@/lib/countryDialCodes";

interface CountryComboboxProps {
  value: string; // ISO 3166-1 alpha-2
  onChange: (iso: string) => void;
  /** Trigger shows flag + dial code (phone fields) instead of flag + name. */
  showDial?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  triggerClassName?: string;
  id?: string;
}

// Searchable country picker over the full generated table (~240 territories).
// "Suggested" pins the tenant's country and our usual markets; "All
// countries" is alphabetical. The search box matches name, dial code and ISO
// at once, "506", "costa" or "cr" all surface Costa Rica. Sized to the
// viewport on phones so the long list scrolls instead of overflowing.
export const CountryCombobox = ({
  value,
  onChange,
  showDial = false,
  placeholder = "Select a country",
  ariaLabel = "Country",
  triggerClassName,
  id,
}: CountryComboboxProps) => {
  const tenant = useTenant();
  const [open, setOpen] = useState(false);
  const selected = findCountryByIso(value);

  const suggested = useMemo(() => {
    const seen = new Set<string>();
    return [tenant.country, ...PREFERRED_ISOS]
      .map((iso) => findCountryByIso(iso))
      .filter((c): c is CountryDialCode => !!c && !seen.has(c.iso) && !!seen.add(c.iso));
  }, [tenant.country]);

  const item = (c: CountryDialCode, group: "suggested" | "all") => (
    <CommandItem
      key={`${group}-${c.iso}`}
      value={`${c.name} ${c.dial} ${c.iso} ${group === "suggested" ? "★" : ""}`}
      onSelect={() => {
        onChange(c.iso);
        setOpen(false);
      }}
    >
      <span className="mr-2 text-base leading-none">{c.flag}</span>
      {showDial && <span className="mr-2 font-medium tabular-nums">{c.dial}</span>}
      <span className="truncate text-muted-foreground">{c.name}</span>
      <Check className={cn("ml-auto h-4 w-4", c.iso === selected?.iso ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn("justify-between px-3 font-normal", triggerClassName)}
        >
          {selected ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-base leading-none">{selected.flag}</span>
              <span className="truncate">{showDial ? selected.dial : selected.name}</span>
            </span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] sm:w-[20rem] p-0" align="start" collisionPadding={16}>
        <Command
          filter={(v, search) => {
            const s = search.toLowerCase().trim();
            return s === "" || v.toLowerCase().includes(s) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search country or code…" />
          <CommandList className="max-h-72">
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup heading="Suggested">{suggested.map((c) => item(c, "suggested"))}</CommandGroup>
            <CommandGroup heading={`All countries (${COUNTRY_DIAL_CODES.length})`}>{COUNTRY_DIAL_CODES.map((c) => item(c, "all"))}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountryCombobox;
